import React, { Fragment, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm, FormProvider, useFormContext } from 'react-hook-form';
import { Button, ButtonGroup, ListGroup, ListGroupItem, Spinner, Collapse, Nav, NavItem, Navbar, CustomInput } from 'reactstrap';
import { useQuery, useMutation, useQueryClient } from 'react-query'
import { toast } from 'components/Shared/Toast';
import clsx from 'clsx';
import produce from 'immer'

import { useProfileState, ProfileCan, useProfileAbility } from 'context/profile'
import { formatDistanceToNow, parseDate } from 'utils'

import bootbox from 'components/Modals/Bootbox'
import { fetchContractCollaborators, sendMessage } from 'api/actions/collaboration'
import { contractAction } from 'api/actions/contracts'
import { fetcher } from 'api/client'

import { TenantMembersSelect } from './TenantMembersSelect';

import { noop } from 'utils';

function Collaborators({ contract = {}, onClose, refetchList = noop }) {
    const { t } = useTranslation();
    const collaboratorsQueryId = ['contract', contract._id, 'collaborators'];
    const [isFetching, setIsFetching] = useState(false);
    const methods = useForm()

    const handleFetchingChange = (isFetching) => {
        setIsFetching(isFetching);
    }

    return (
        <FormProvider {...methods}>
            <div className="h-100 d-flex flex-column">
                {/* <div id="plan" name="plan" className="plan">
                    <h2>{t('wordList.plans.upgrade')}</h2>
                    <p>{t('wordList.plans.availableOnPE')}</p>
                    <p className="know-more">{t('wordList.plans.knowmore')}</p>
                    
                    <a className="btn btn-sm btn-white" href="https://trato.io#plans" target="_blank">{t('wordList.plans.see_plans')}</a>
                </div>
                <div id="blur" name="blur" /> */}
                <div className="detail px-3 pt-3 pb-2 text-justify">
                    <p>{t('contracts.collaborators.description')}</p>
                </div>
                <div className="collaborators-search-wrapper px-3 pb-3">
                    {/* solo si es owner sale  // whoami.isOwner() */}
                    <ProfileCan I="update" this={contract} field="watchers">
                        {() =>
                            <CollaboratorsSelect
                                contract={contract}
                                collaboratorsQueryId={collaboratorsQueryId}
                                onFetchingChange={handleFetchingChange}
                            />
                        }
                    </ProfileCan>

                </div>
                <CollaboratorsToolbar
                    contract={contract}
                    collaboratorsQueryId={collaboratorsQueryId}
                    refetchList={refetchList}
                    onFetchingChange={handleFetchingChange}
                    onClose={onClose}
                />
                {isFetching && <FetchingProgressLoader />}
                <CollaboratorList
                    contract={contract}
                    collaboratorsQueryId={collaboratorsQueryId}
                    onFetchingChange={handleFetchingChange}
                />
            </div>
        </FormProvider>
    )
};

export default Collaborators;

export function FetchingProgressLoader() {
    return (
        <div>
            <div className="trato-fetching-progress progress-moved pulse-animation">
                <div className="trato-fetching-progress-bar" >
                </div>
            </div>
            {/* <div className="d-flex justify-content-between text-primary px-2 py-1">
                    <small>Creando...</small>
                    <Spinner size="xs" />
                </div> */}
        </div>
    )
}

export function CollaboratorsSelect({ contract, collaboratorsQueryId, onFetchingChange }) {
    const { t } = useTranslation();
    const { profile } = useProfileState()
    const queryClient = useQueryClient();
    const { mutate: mutateContractAction, isLoading } = useMutation(({ contractId, action, ...payload }) => fetcher(contractAction(contractId, action, payload)), {
        onMutate: () => {
            onFetchingChange(true)
        },
        onSuccess: (data) => {
            queryClient.invalidateQueries(collaboratorsQueryId)
        },
        onError: (error) => {
            toast.error(t(`errors.${error.message}`, t('errors.GeneralError')))
        },
        onSettled: () => {
            onFetchingChange(false)
        }
    })

    const handleCollaboratorChange = async (option, { action }) => {
        if (action === 'select-option') {
            const isWatcher = contract.watchers.find(watcher => watcher._id === option.value);
            const isGroup = option.isGroup;
            const isMe = option.value === profile._id;

            if (isWatcher) {
                return toast.error(t('contracts.collaborators.collaborator_exists'));
            } else if (isMe) {
                return toast.error(t('contracts.collaborators.collaborator_self'));
            } else if (isGroup) {
                mutateContractAction({
                    contractId: contract._id,
                    action: 'addgroup',
                    groupId: option.value
                }, {
                    onSuccess: (data) => {
                        queryClient.setQueryData(collaboratorsQueryId, produce(draft => {
                            draft.push(data.group)
                        }))
                    }
                })

                //   this.reFetchModel();
            } else {
                mutateContractAction({
                    contractId: contract._id,
                    action: 'addcollaborator',
                    collaboratorId: option.value
                }, {
                    onSuccess: (data, variables) => {
                        queryClient.invalidateQueries(['contract', variables.contractId])
                        queryClient.setQueryData(collaboratorsQueryId, produce(draft => {
                            draft.push(data.collaborator)
                        }))
                    }
                })
            }
        }
    }


    return (
        <TenantMembersSelect
            // className="mx-3"
            withGroups
            contractId={contract._id}
            isDisabled={isLoading}
            onChange={handleCollaboratorChange}
        />
    )
}

function CollaboratorsToolbar({ contract, collaboratorsQueryId, refetchList, onFetchingChange, onClose }) {
    const { profile, roles } = useProfileState();
    const queryClient = useQueryClient();
    const { t } = useTranslation(['translation', 'validations']);
    const { watch, reset } = useFormContext();
    const selected = watch()
    const profileAbility = useProfileAbility()

    const { hasRequestedVobo, isChild, hasWorkflow, isNegotiation } = useMemo(() => {
        const hasRequestedVobo = contract?.status === 'APPROVED' || contract?.vobo?.length > 0 || false;
        const isChild = !!contract.parentReference;
        const hasWorkflow = !!contract.workflow;
        const isLegal = profile.groups?.some(g => g.legal === true) ?? false;
        const isNegotiation = profile.groups?.some(g => g.negotiation === true) ?? false;

        return { hasRequestedVobo, isChild, hasWorkflow, isLegal, isNegotiation }
    }, [contract, profile.groups])

    const collaborators = useMemo(() => {
        const collaboratorList = queryClient.getQueryData(collaboratorsQueryId);

        return collaboratorList?.filter(c => selected[c._id]) ?? []
    }, [selected, collaboratorsQueryId])

    const [hasSelected, hasSelectedMultiple, collaboratorHasVobo] = useMemo(() => {
        const collaboratorHasVobo = collaborators?.some(c => Object.keys(c?.vobo ?? {}).length);
        return [collaborators.length, collaborators.length > 1, collaboratorHasVobo]
    }, [collaborators])

    const resetCollaborators = useCallback(() => {
        const resetValues = collaborators.reduce((obj, collaborator) => {
            obj[collaborator._id] = false;
            return obj;
        }, {})
        reset(resetValues)
    }, [collaborators, reset])

    const { mutate: mutateContractAction } = useMutation(({ contractId, action, ...payload }) => fetcher(contractAction(contractId, action, payload)), {
        onMutate: () => {
            onFetchingChange(true)
        },
        onSuccess: (data, variables) => {
            queryClient.invalidateQueries(collaboratorsQueryId)
            queryClient.invalidateQueries(['contract', variables.contractId])
            resetCollaborators()
        },
        onError: (error) => {
            toast.error(t(`errors.${error.message}`, t('errors.GeneralError')))
        },
        onSettled: () => {
            onFetchingChange(false)
        }
    })
    const { mutate: mutateSendMessage } = useMutation((payload) => fetcher(sendMessage(payload)), {
        onMutate: () => {
            onFetchingChange(true)
        },
        onSuccess: (data, variables) => {
            if (variables.notifySuccess) {
                toast.success(t('contracts.collaborators.send_success'));
                queryClient.invalidateQueries(collaboratorsQueryId)
            }
            resetCollaborators()
        },
        onError: (error) => {
            toast.error(t(`errors.${error.message}`, t('errors.GeneralError')))
        },
        onSettled: () => {
            onFetchingChange(false)
        }
    })


    const handleRequestApproval = async () => {
        if (!collaborators || !collaborators.length) return;
        const collabLabel = (collaborators.length > 1 ? `${collaborators.length} colaboradores` : ' 1 colaborador');
        const resp = await bootbox.confirm(t('contracts.collaborators.approval_confirm', { collabLabel, contractName: contract.name }));
        if (!resp) return;
        mutateContractAction({
            contractId: contract._id,
            action: 'askmultivobo',
            collaborators,
        })
    }

    const handleCancelApprovals = async () => {
        const resp = await bootbox.confirm(t('editor.collaboration.confirm_delete'));
        if (!resp) return;
        mutateContractAction({
            contractId: contract._id,
            action: 'cancelvobos',
        }, {
            onSuccess: (data) => {
                toast.success(t('editor.collaboration.confirm_success'))
            }
        })
    }

    const handleSendMessage = async (evt, message) => {
        const payload = { message, contractId: contract._id, collaborators, notifySuccess: false };

        if (!message) {
            const msg = await bootbox.prompt({
                title: t('contracts.collaborators.insert_message'),
                inputType: 'textarea',
            });
            if (!msg) return;

            payload.message = msg;
            payload.notifySuccess = true;
        }

        mutateSendMessage(payload)
    }

    const doDelegateContract = ({ message, stage } = {}) => {
        const collaborator = collaborators.shift();
        const contractId = contract._id;

        if (collaborator.email) { // INDIVIDUAL DELEGATION
            mutateContractAction({
                contractId,
                action: 'delegatecontract',
                newOwnerId: collaborator._id,
                delegateLinkContracts: true,
                message,
                stage,
            }, {
                onSuccess: (data) => {
                    refetchList()
                    onClose()
                }
            })
        } else if (collaborator.group) { // GROUP DELEGATION
            mutateContractAction({
                contractId,
                action: 'delegatecontractgroup',
                userGroup: collaborator.group._id,
                message,
                stage,
            }, {
                onSuccess: (data) => {
                    refetchList()
                    onClose()
                }
            })
        }
    }

    const handleDelegateContract = async () => {
        if (!collaborators || !collaborators.length) return;

        bootbox.prompt({
            title: t('contracts.collaborators.delegate_confirm'),
            inputType: 'textarea',
            placeholder: t('contracts.collaborators.delegate_message'),
            buttons: {
                cancel: {
                    label: t('dashboard.messages.cancel'),
                    color: 'secondary',
                },
                delegate: {
                    label: t('editor.collaboration.delegate'),
                    color: 'primary',
                    callback: async (message) => {
                        doDelegateContract({ message })
                    }
                },
            },
        });


    }

    const handleAssignContract = async () => {
        if (!collaborators || !collaborators.length) return;

        const resp = await bootbox.confirm(t('contracts.collaborators.assign_confirm'));
        if (!resp) return;

        doDelegateContract({ stage: 'assign' })
    }

    const handleRequestCorrection = async () => {
        if (!collaborators || !collaborators.length) return;

        const message = await bootbox.prompt(t('contracts.collaborators.reason'));
        if (!message) return;

        doDelegateContract({ stage: 'correction', message })
    }

    const handleRemoveCollaborator = async () => {
        const contractId = contract._id;
        const collaborator = collaborators.shift();

        if (collaborator.email) { // INDIVIDUAL REMOVE
            const resp = await bootbox.confirm(t('contracts.collaborators.remove_person', { replace: { givenName: collaborator.givenName, familyName: collaborator.familyName } }));
            if (!resp) return;

            mutateContractAction({
                contractId,
                action: 'removecollaborator',
                collaboratorId: collaborator._id,
            }, {
                onSuccess: () => {
                    toast.success(t('validations:messages.successfullyDeleted', { context: 'male', entity: 'colaborador' }))
                }
            })
        } else if (collaborator.group) { // GROUP REMOVE
            const resp = await bootbox.confirm(t('', { replace: { groupName: collaborator.group?.name } }));
            if (!resp) return;

            mutateContractAction({
                contractId,
                action: 'removegroupcollaborator',
                groupId: collaborator.group._id,
            }, {
                onSuccess: () => {
                    toast.success(t('validations:messages.successfullyDeleted', { context: 'male', entity: 'grupo' }))
                }
            })
        }
    }


    return (
        <ButtonGroup className="d-flex align-items-center bg-grey border-top border-bottom">

            { hasRequestedVobo &&
                <CollaboratorsToolbarItem
                    icon="times"
                    contract={contract}
                    isDisabled={hasSelected || hasWorkflow}
                    onClick={handleCancelApprovals}
                    data-action="cancel-approvals"
                >
                    {t('contracts.collaborators.cancel')}<br />{t('contracts.collaborators.approvals')}
                </CollaboratorsToolbarItem>
            }
            <CollaboratorsToolbarItem
                icon={'thumbs-up'} //hasWorkflow ? 'ban' : 
                data-action="request-approval"
                contract={contract}
                isDisabled={!hasSelected || collaboratorHasVobo || (contract.workflowStep > 0 && !contract.workflowStopped)} // profileAbility.cannot('delegate-in-collaboration', 'Contract')
                onClick={handleRequestApproval}
            // title={profileAbility.cannot('delegate-in-collaboration', 'Contract') ? t('wordList.plans.nonAvailableOnPlan') : null}
            >
                {t('contracts.collaborators.ask')}<br />{t('contracts.collaborators.approval')}
            </CollaboratorsToolbarItem>

            <ProfileCan I="request-correction" on="Contract">
                {() =>
                    <CollaboratorsToolbarItem
                        icon="eye"
                        contract={contract}
                        isDisabled={!hasSelected || hasSelectedMultiple || isChild}
                        onClick={handleRequestCorrection}
                        data-action="request-correction"
                    >
                        {t('contracts.collaborators.ask')}<br />{t('contracts.collaborators.revision')}
                    </CollaboratorsToolbarItem>
                }
            </ProfileCan>
            <CollaboratorsToolbarItem
                icon="reply"
                contract={contract}
                isDisabled={!hasSelected || hasSelectedMultiple || isChild} //profileAbility.cannot('send-vobo-in-collaboration', 'Contract')
                onClick={handleDelegateContract}
                data-action="delegate-contract"
            // title={profileAbility.cannot('send-vobo-in-collaboration', 'Contract') ? t('wordList.plans.nonAvailableOnPlan') : null}
            >
                {/*{{#cant 'SEND_VOBO_IN_COLLABORATION'}}title="{t('wordList.plans.nonAvailableOnPlan')}"{{/cant}}*/}
                {t('contracts.collaborators._delegate')}<br />{t('contracts.collaborators.contract')}
            </CollaboratorsToolbarItem>
            <CollaboratorsToolbarItem
                icon="envelope"
                contract={contract}
                isDisabled={!hasSelected}
                isPublic
                onClick={handleSendMessage}
                data-action="send-message"
            >
                {t('contracts.collaborators.send')}<br />{t('contracts.collaborators.message')}
            </CollaboratorsToolbarItem>
            <CollaboratorsToolbarItem
                icon="trash"
                contract={contract}
                isDisabled={!hasSelected || hasSelectedMultiple || isChild}
                onClick={handleRemoveCollaborator}
                data-action="remove-collaborator"
            >
                {t('contracts.collaborators._delete')}<br />{t('contracts.collaborators.collaborator')}
            </CollaboratorsToolbarItem>

            {!isNegotiation &&
                <ProfileCan I="assign" a="Contract">
                    {() =>
                        <CollaboratorsToolbarItem
                            icon="edit"
                            contract={contract}
                            isDisabled={!hasSelected || hasSelectedMultiple || isChild} //profileAbility.cannot('assign-in-collaboration', 'Contract')
                            onClick={handleAssignContract}
                            data-action="assign-contract"
                        // title={profileAbility.cannot('assign-in-collaboration', 'Contract') ? t('wordList.plans.nonAvailableOnPlan') : null}
                        >
                            {t('contracts.collaborators.assign')}<br />{t('contracts.collaborators.contract')}
                        </CollaboratorsToolbarItem>
                    }
                </ProfileCan>
            }
        </ButtonGroup>
    )
}

function CollaboratorsToolbarItem({ contract, icon, isPublic, isDisabled, onClick = noop, children }) {
    const getDisableState = (allowed) => {
        if (isPublic) return false
        return !allowed
    }

    return (
        <ProfileCan I="modify" this={contract} field="collaboration" passThrough>
            {allowed => {
                const shouldDisable = getDisableState(allowed)


                return <Button
                    size="sm"
                    className="pointer text-center rounded-0"
                    color="light"
                    data-action="cancel-approvals"
                    onClick={onClick}
                    disabled={isDisabled || shouldDisable}
                >
                    <i className={`fas fa-${icon} text-muted `}></i> <br></br>
                    <span className="mt-1 text-muted">{children}</span>
                </Button>
            }}
        </ProfileCan>

    )
}


function CollaboratorList({ contract, collaboratorsQueryId, onFetchingChange }) {
    const { t } = useTranslation();
    const queryClient = useQueryClient();
    const { data, status } = useQuery(collaboratorsQueryId, ({ queryKey: [, contractId] }) => fetcher(fetchContractCollaborators({ contractId })), {
        enabled: !!contract._id
    })
    const { mutate: mutateRemoveVobo } = useMutation(({ contractId, ...payload }) => fetcher(contractAction(contractId, 'removevobo', payload)), {
        onMutate: () => {
            onFetchingChange(true)
        },
        onSuccess: (data, variables) => {
            toast.success(t('editor.collaboration.vobo_revoke_success'));
            // TODO: handle better vobo elimination maybe use onMutate for better ux
            queryClient.setQueryData(['contract', contract._id, 'collaborators'], produce(draft => {
                const collaboratorId = variables.voboGroup || variables.voboUser;
                const index = draft.findIndex(todo => todo._id === collaboratorId)
                if (index !== -1) draft[index].vobo = {}
            }))
            queryClient.invalidateQueries(['contract', contract._id, 'collaborators'])
        },
        onError: (error) => {
            toast.error(t(`errors.${error.message}`, t('errors.GeneralError')))
        },
        onSettled: () => {
            onFetchingChange(false)
        }
    })

    const handleRevokeApproval = async (collaborator) => {
        const { group, givenName, familyName, vobo } = collaborator;
        if (vobo.status === 'sent') {

            const userName = group ? group.name : `${givenName} ${familyName}`;

            const resp = await bootbox.confirm(t('editor.collaboration.approve_revoke_confirm', { userName }))
            if (!resp) return;

            const payload = {
                contractId: contract._id,
            }

            if (group) {
                payload.voboGroup = group._id;
            } else {
                payload.voboUser = collaborator._id;
            }


            await mutateRemoveVobo(payload)
        }
    }


    return (
        <ListGroup style={{ overFlow: 'auto' }}>
            {
                data?.map((collaborator, i) => {
                    const isLast = data.length - 1 === i

                    return <CollaboratorItem
                        key={collaborator._id}
                        className={clsx(isLast && 'mb-5')}
                        contract={contract}
                        collaborator={collaborator}
                        onRevokeApproval={handleRevokeApproval}
                    />
                })
            }

        </ListGroup>
    )
}


function CollaboratorItem({ className, collaborator, onRevokeApproval }) {
    const { t } = useTranslation();
    const [isOpen, setIsOpen] = useState(false);
    const { register } = useFormContext();

    const { group, givenName, familyName, vobo, canOwn, lastStatus, wasAssigned, events } = collaborator;
    const icon = group ? 'users' : 'user';

    const voboState = useMemo(() => {
        const voboMapper = {
            'sent': {
                icon: 'exclamation-triangle',
                color: 'warning',
                tooltip: 'editor.collaboration.pending'
            },
            'approved': {
                icon: 'thumbs-up',
                color: 'success',
                tooltip: 'editor.collaboration.approved'
            },
            'rejected': {
                icon: 'thumbs-down',
                color: 'danger',
                tooltip: 'editor.collaboration.rejected'
            }
        }

        return voboMapper[vobo?.status ?? '']
    }, [vobo])

    const toggle = () => setIsOpen(isOpen => !isOpen)

    const handleRevokeApproval = () => {
        onRevokeApproval(collaborator)
    }


    return (
        <ListGroupItem hidden={wasAssigned}>
            <div className="d-flex">
                <div className="mr-2">
                    <CustomInput type="checkbox" id={collaborator._id} name={collaborator._id} innerRef={register} />
                </div>
                <div className="mr-2">
                    <i className={`fas fa-${icon} text-muted`} />
                </div>

                <div className="mr-2">
                    <span className="collaborator-info-name text-muted mr-1">
                        {givenName} {familyName} {group?.name}
                        {voboState &&
                            <i
                                className={`fas fa-${voboState.icon} text-${voboState.color}`}
                                data-action="revoke-approval{{#if group}}-group{{/if}}"
                                onClick={handleRevokeApproval}
                                title={t(voboState.tooltip)}
                                data-html="true"
                            />
                        }
                        {canOwn &&
                            <i className="fas fa-hand-paper" title={t('editor.collaboration.group-delegated')} />
                        }
                    </span>
                    <div className="collaborator-info-update">
                        {lastStatus &&
                            <Fragment>
                            <em><b>{lastStatus.who}</b> {t(`events.${lastStatus.type}`)}{' '}
                            {(lastStatus.toTranslated || lastStatus.to || lastStatus.user_to) &&
                                <b>{
                                    lastStatus.toTranslated ? lastStatus.toTranslated
                                        : lastStatus.user_to?.givenName ? <span>{lastStatus.user_to?.givenName} {lastStatus.user_to?.familyName}</span>
                                            : lastStatus.to}
                                </b>
                            }</em>
                            {' '}<span className="text-muted">{formatDistanceToNow(lastStatus.eventDate)}</span></Fragment>
                        }
                    </div>
                </div>
                <div className="ml-auto">
                    <i className={`fas pointer fa-chevron-${isOpen ? 'up' : 'down'}`} onClick={toggle}></i>
                </div>
            </div>
            <TimelineEventsList events={events} isOpen={isOpen} />
        </ListGroupItem>
    )
}

export function TimelineEventsList({ events, isOpen }) {
    const { t } = useTranslation();

    if (!events?.length) {
        return <span className="empty-events">{t('contracts.listing.no_events')}</span>
    }

    return (
        <Collapse isOpen={isOpen} className="border mt-2 bg-light">
            <ul className="timeline">
                {events?.map(event => {
                    return <TimelineEventItem event={event} />
                })}
            </ul>
        </Collapse>
    )
}

function TimelineEventItem({ event }) {
    const { t } = useTranslation();
    const { type, message, who, eventDate, properties, group_to, toTranslated, to, user_to } = event;

    return (
        <li key={event._id} className="timeline-item" date-is={parseDate(eventDate)}>
        <p>
            {message ?
                message
                :
                <Fragment>
                    <b>{who}</b> {t(`events.${type}`)}{' '}
                    {(toTranslated || to || user_to) &&
                        <b>{
                            toTranslated ? toTranslated
                                : user_to?.givenName ? <span>{user_to?.givenName} {user_to?.familyName}</span>
                                    : to}
                        </b>
                    }
                    <b>{properties?.document}</b>
                    {type === 'extra' && <b>{properties?.filename}</b>}
                    {type === 'set-expire' && <span><b>{properties?.attachment}</b> {t('events.set-expire-cont')} <b>{parseDate(properties?.date)}</b></span>}
                </Fragment>
            }

            {['add-collaborator', 'remove-collaborator', 'vobo-revoked'].includes(type) &&
                <b>{properties?.collaborator}</b>
            }
            {['document-replace', 'create-template', 'create-questionnaire'].includes(type) &&
                <b>{properties?.filename}</b>
            }

            {type === 'collab-message' &&
                <Fragment><br /><strong>"{properties?.comment}"</strong></Fragment>
            }

            {['vobo-approved', 'vobo-rejected', 'grab-vobo'].includes(type) && group_to &&
                <span>{t('events.vobo-representing')} <b>{group_to.name}</b></span>
            }
            <div className="text-muted">
                {formatDistanceToNow(eventDate)}
            </div>
        </p>
        
    </li>
    )
}